import React, { createContext, useContext, useState, useEffect } from "react";
import { Team, Match } from "../interfaces";
import { API_ENDPOINTS } from "../apiConfig";

// Fix Team type in TeamScheduleContext to use 'name' instead of 'team'
// export type Team = {
//   name: string;
//   id: number;
//   matches: Match[];
// };

type TeamScheduleView = "all" | "home" | "away";

interface TeamScheduleContextType {
  view: TeamScheduleView;
  setView: (v: TeamScheduleView) => void;
  filteredMatches: Match[];
  team: Team | null;
  loading: boolean;
  error: string | null;
}

const TeamScheduleContext = createContext<TeamScheduleContextType | undefined>(
  undefined
);

export const useTeamScheduleContext = () => {
  const ctx = useContext(TeamScheduleContext);
  if (!ctx)
    throw new Error(
      "useTeamScheduleContext must be used within TeamScheduleProvider"
    );
  return ctx;
};

export const useOptionalTeamScheduleContext = () => {
  return useContext(TeamScheduleContext);
};

interface TeamScheduleProviderProps {
  teamId?: number;
  seasonId?: number;
  team?: Team;
  children: React.ReactNode;
}

export const TeamScheduleProvider: React.FC<TeamScheduleProviderProps> = ({
  teamId,
  seasonId,
  team: teamProp,
  children,
}) => {
  const [view, setView] = useState<TeamScheduleView>("all");
  const [team, setTeam] = useState<Team | null>(teamProp || null);
  const [filteredMatches, setFilteredMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState<boolean>(!teamProp);
  const [error, setError] = useState<string | null>(null);

  // If a team prop is provided, use it directly and skip fetching
  useEffect(() => {
    if (teamProp) {
      setTeam(teamProp);
      setLoading(false);
      setError(null);
      return;
    }
    if (teamId == null || seasonId == null) {
      setTeam(null);
      setLoading(false);
      setError("Kein Team oder Saison ausgewählt");
      return;
    }
    
    // Fetch team matches when teamId and seasonId are provided
    setLoading(true);
    const fetchTeamMatches = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.matchesByTeam(seasonId, teamId));
        if (!res.ok) throw new Error("Fehler beim Laden der Spiele");
        const data = await res.json();
        // Ensure the team object has the id property for filtering
        setTeam({ ...data, id: teamId });
        setError(null);
      } catch (err: any) {
        setError(err.message || "Fehler beim Laden");
        setTeam(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeamMatches();
  }, [teamId, seasonId, teamProp]);

  // Filter matches when team or view changes
  useEffect(() => {
    if (!team || !team.matches) {
      setFilteredMatches([]);
      return;
    }
    
    console.log('Filtering matches:', { view, teamId: team.id, totalMatches: team.matches.length });
    
    if (view === "home") {
      const filtered = team.matches.filter((m) => m.team_home.id === team.id);
      console.log('Home matches:', filtered.length);
      setFilteredMatches(filtered);
    } else if (view === "away") {
      const filtered = team.matches.filter((m) => m.team_away?.id === team.id);
      console.log('Away matches:', filtered.length);
      setFilteredMatches(filtered);
    } else {
      console.log('All matches:', team.matches.length);
      setFilteredMatches(team.matches);
    }
  }, [team, view]);

  return (
    <TeamScheduleContext.Provider
      value={{ view, setView, filteredMatches, team, loading, error }}
    >
      {children}
    </TeamScheduleContext.Provider>
  );
};
